import React, { FC, useEffect, useMemo, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import {
  Button,
  Card,
  CardBody,
  Col,
  DropdownItem,
  DropdownMenu,
  DropdownToggle,
  FormGroup,
  Input,
  Label,
  Row,
  UncontrolledDropdown,
} from 'reactstrap';
import { ProductCategoryModel } from '../../../../shared/orders/product';
import { ElementSelector, MaterialIcon, PermissionBoundary } from '../../../components';
import { JSONForm, PreSubmitData } from '../../../components/json-form/JSONForm';
import { useTranslation } from '../../../translations';
import { useBoolState } from '../../../utils';
import { captureError } from '../../../utils/errorHandling';
import { CategoryBadges } from './CategoryBadges';
import { LocalSearchProvider } from './utils';

interface CategoryItemProps {
  readonly category: ProductCategoryModel;
  readonly onUpdate: () => void;
  readonly redirectFn: (url: string) => void;
}

interface DeleteFormElements {
  deleteItems?: boolean;
  replacementCategoryId?: number;
}

export const CategoryItem: FC<CategoryItemProps> = ({ category, onUpdate, redirectFn }) => {
  const { ts } = useTranslation();
  const [isDeleteMode, enableDeleteMode, disableDeleteMode] = useBoolState(false);
  const [selectedId, setSelectedId] = useState<number>();
  const [visibility, enableToggleVisibility, disableToggleVisibility] = useBoolState(
    category.isStore,
  );

  const didMount = useRef(false);

  useEffect(() => {
    if (!didMount.current) {
      didMount.current = true;
      return;
    }

    api.updateCategory(category.id, visibility).then(onUpdate, captureError);
  }, [visibility]);

  const searchProvider = useMemo(
    () => new LocalSearchProvider(async () => await api.getProductCategories()),
    [],
  );

  if (isDeleteMode) {
    return (
      <Card>
        <CardBody>
          <MaterialIcon
            className="float-end pointer"
            id="close"
            name="close"
            onClick={disableDeleteMode}
          />
          <h5>
            {ts('category_item.delete_category')}: {category.name}{' '}
            <small>(ID: {category.id})</small>
          </h5>
          <p>{ts('category_item.in_order_to_delete_this')}</p>
          <p>{ts('category_item.deleting_a_category_is_a')}</p>
          <hr />
          <JSONForm<object, DeleteFormElements>
            method="delete"
            onSuccess={onUpdate}
            path={`/api/products/categories/${category.id}`}
            preSubmit={deletePreSubmit}
          >
            <Row>
              <Col lg={5} xs={12}>
                <FormGroup>
                  <FormGroup check className="mt-10 start-align">
                    <Input id="deleteItems" name="deleteItems" type="checkbox" />
                    <Label check for="deleteItems">
                      {ts('category_item.delete_all_existing_items')}
                    </Label>
                  </FormGroup>
                </FormGroup>
              </Col>
              <Col lg={2} xs={12}>
                <h2>{ts('common.or')}</h2>
              </Col>
              <Col lg={5} xs={12}>
                <FormGroup>
                  <Label for="newCategory">{ts('common.new_category')}</Label>
                  <ElementSelector<ProductCategoryModel>
                    id="categorySelector"
                    maxItems={1}
                    searchProvider={searchProvider}
                    singleSelectionIdChanged={setSelectedId}
                  />
                  <Input
                    id="replacementCategoryId"
                    name="replacementCategoryId"
                    type="hidden"
                    value={selectedId ?? ''}
                  />
                </FormGroup>
              </Col>
            </Row>
            <Button color="danger" id="submitDeleteCategory">
              {ts('category_item.delete_category')}
            </Button>
          </JSONForm>
        </CardBody>
      </Card>
    );
  }

  return (
    <Card>
      <CardBody>
        <div
          style={{
            minWidth: '200px',
            position: 'absolute',
            right: '15px',
            textAlign: 'right',
            top: '15px',
          }}
        >
          <UncontrolledDropdown className="mw-100">
            <DropdownToggle tag="a">
              <MaterialIcon className="pointer" id="moreOptions" name="more_horiz" />
            </DropdownToggle>
            <DropdownMenu end>
              {!category.isRegistration && (
                <>
                  <DropdownItem
                    className="text-danger"
                    id="deleteCategory"
                    onClick={enableDeleteMode}
                  >
                    {ts('category_item.delete_category')}
                  </DropdownItem>
                  <hr />
                </>
              )}

              <PermissionBoundary inline requiredPermissions="audit:read">
                <DropdownItem
                  tag={Link}
                  to={`/housekeeping/auditlog/ProductCategory/${category.id}`}
                >
                  {ts('category_item.view_history')}
                </DropdownItem>
              </PermissionBoundary>
              {category.isStore ? (
                <DropdownItem id="hideCategory" onClick={disableToggleVisibility}>
                  {ts('category_item.hide_category')}
                </DropdownItem>
              ) : (
                <DropdownItem id="showCategory" onClick={enableToggleVisibility}>
                  {ts('category_item.show_category')}
                </DropdownItem>
              )}
            </DropdownMenu>
          </UncontrolledDropdown>
        </div>
        <h4 style={{ marginRight: '30px' }}>
          {category.name} <small>(ID: {category.id})</small>
        </h4>
        <div className="float-end">
          <Button
            color="primary"
            id="viewProducts"
            onClick={() => {
              redirectFn(`category/${category.id}`);
            }}
            outline
          >
            {ts('category_item.view_products')}
          </Button>
        </div>
        <div>
          <CategoryBadges category={category} />
        </div>
      </CardBody>
    </Card>
  );
};

const deletePreSubmit = ({ inputs, requestOptions }: PreSubmitData<DeleteFormElements>) => {
  requestOptions.query = inputs.deleteItems
    ? {
        deleteItems: true,
      }
    : {
        replacementCategoryId: inputs.replacementCategoryId ?? 0,
      };
};
